// Small presentational building blocks shared across screens.
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

/** Join class lists; when two utilities conflict, the later one wins. */
export const cn = twMerge;

/*
 * Buttons. Screens compose these with cn() and add their own layout on top,
 * e.g. cn(btn, btnPrimary, 'w-full').
 */
export const btn =
  'flex items-center justify-center gap-[7px] min-h-[38px] py-2 px-[14px] border border-transparent rounded-[8px] cursor-pointer ' +
  '[transition:background_140ms,border-color_140ms,color_140ms,box-shadow_140ms,transform_100ms] ' +
  'active:enabled:translate-y-px disabled:opacity-55 disabled:cursor-not-allowed';

export const btnPrimary =
  'bg-aqua border-aqua text-midnight shadow-[0_2px_5px_rgba(14,26,43,0.16)] ' +
  'enabled:hover:shadow-[0_4px_10px_rgba(14,26,43,0.18)] dark:enabled:hover:bg-[#49ddca] dark:enabled:hover:border-[#49ddca]';

export const btnGhost =
  'bg-surface border-edge text-fg-2 enabled:hover:bg-accent-soft enabled:hover:border-aqua enabled:hover:text-aqua';

export const btnDanger =
  'bg-[#dc2626] border-[#dc2626] text-white enabled:hover:bg-[#b91c1c] enabled:hover:border-[#b91c1c] enabled:hover:brightness-[.94]';

export const btnSm = 'min-h-8 py-1.5 px-2.5';

export const Spinner = () => (
  <span
    className="inline-block size-4 shrink-0 rounded-full border-2 border-edge-strong border-t-primary motion-safe:animate-[spin_0.7s_linear_infinite]"
    aria-hidden="true"
  />
);

export function Splash({
  message = 'Loading...',
}: {
  message?: string;
}) {
  return (
    <main className="relative z-1 flex min-h-screen w-full flex-col items-center justify-center justify-items-center text-primary dark:z-2">
      <div className="flex items-center justify-center gap-[13px]" aria-label="nxtCloud">
        <span className="flex items-center justify-center" aria-hidden="true">
          <svg className="block size-14 align-baseline" viewBox="0 0 32 32" width="56" height="56">
            <rect
              width="32"
              height="32"
              rx="9"
              fill="currentColor"
            />
            <path
              d="M7 20c2.4-7 4.6-9 6.6-9s2.2 7 4.2 7 3-4.5 6.2-4.5"
              stroke="var(--surface)"
              strokeWidth="2.4"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>

        <span className="text-[34px] leading-none font-[750] tracking-[-0.04em] text-fg">
          nxt<span className="text-accent">Cloud</span>
        </span>
      </div>

      <div className="mt-7 flex items-center justify-center gap-2.5 text-[14px] text-fg-3">
        <span
          className="size-[22px] rounded-[50%] border-3 border-edge border-t-primary animate-[spin_0.8s_linear_infinite]"
          aria-hidden="true"
        />
        <span>{message}</span>
      </div>
    </main>
  );
}

type PillKind =
  | 'ok'
  | 'warn'
  | 'danger'
  | 'muted'
  | 'info';

const PILL: Record<PillKind, string> = {
  ok: 'bg-accent-soft text-aqua',
  info: 'bg-accent-soft text-aqua',
  muted: 'bg-surface-3 text-fg-3',
  warn: 'bg-warn-soft text-warn',
  danger: 'bg-danger-soft text-danger',
};

export const pill =
  'inline-flex items-center min-h-6 py-[3px] px-2.5 rounded-[999px] text-[11px] font-bold leading-[1.5] whitespace-nowrap';

export function Pill({
  kind,
  children,
  className,
}: {
  kind: PillKind;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={cn(pill, PILL[kind], className)}>
      {children}
    </span>
  );
}

export const Muted = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <span className={cn('text-fg-3', className)}>
    {children}
  </span>
);

const stateBox = 'px-6 py-[46px] text-center';
const stateIcon = 'mb-2 text-[26px] leading-none text-fg-3';
const stateTitle = 'mt-0 mb-1.5 text-[16px] leading-[1.4] font-semibold tracking-normal';
const stateMessage = 'mx-auto mb-3.5 max-w-[46ch] text-fg-3';

export function EmptyState({
  title,
  message,
}: {
  title: string;
  message: string;
}) {
  return (
    <div className={stateBox}>
      <div className={stateIcon} aria-hidden="true">
        ∅
      </div>

      <h2 className={stateTitle}>
        {title}
      </h2>

      <p className={stateMessage}>
        {message}
      </p>
    </div>
  );
}

export function ErrorState({
  title,
  message,
  onRetry,
}: {
  title: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className={stateBox}>
      <div className={cn(stateIcon, 'text-danger')} aria-hidden="true">
        !
      </div>

      <h2 className={stateTitle}>
        {title}
      </h2>

      <p className={stateMessage}>
        {message}
      </p>

      {onRetry && (
        <button
          type="button"
          className={cn(btn, btnGhost, 'mx-auto inline-flex')}
          onClick={onRetry}
        >
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * Placeholder rows sized like the real table,
 * so the layout does not jump while data loads.
 */
export function TableSkeleton({
  rows = 6,
}: {
  rows?: number;
}) {
  return (
    <div className="px-4 py-3.5" aria-hidden="true">
      {Array.from(
        { length: rows },
        (_, index) => (
          <div
            key={index}
            className="mb-2 h-[42px] rounded-[8px] bg-[linear-gradient(90deg,color-mix(in_srgb,var(--text-3)_10%,transparent)_25%,color-mix(in_srgb,var(--text-3)_20%,transparent)_37%,color-mix(in_srgb,var(--text-3)_10%,transparent)_63%)] bg-size-[400%_100%] motion-safe:animate-[shimmer_1.4s_ease_infinite]"
          />
        ),
      )}
    </div>
  );
}

export type IconName =
  | 'home'
  | 'recordings'
  | 'list'
  | 'code'
  | 'gear'
  | 'user'
  | 'signout'
  | 'eye'
  | 'download'
  | 'upload'
  | 'trash'
  | 'search'
  | 'filter'
  | 'clock'
  | 'check'
  | 'database'
  | 'file'
  | 'refresh'
  | 'close'
  | 'ruler'
  | 'foot'
  | 'tune';

const PATHS: Record<
  IconName,
  ReactNode
> = {
  home: (
    <path d="M3 10.5 12 3l9 7.5V21H3z" />
  ),

  recordings: (
    <path d="M3 7.5a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7.5z" />
  ),

  list: (
    <path d="M4 5h16M4 12h16M4 19h16" />
  ),

  code: (
    <path d="m8 6-6 6 6 6M16 6l6 6-6 6" />
  ),

  gear: (
    <>
      <circle
        cx="12"
        cy="12"
        r="3.2"
      />

      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" />
    </>
  ),

  user: (
    <>
      <circle
        cx="12"
        cy="8"
        r="3.5"
      />

      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
    </>
  ),

  signout: (
    <path d="M15 12H4m0 0 3.5-3.5M4 12l3.5 3.5M11 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-7" />
  ),

  eye: (
    <>
      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />

      <circle
        cx="12"
        cy="12"
        r="2.6"
      />
    </>
  ),

  download: (
    <path d="M12 3v11m0 0 4-4m-4 4-4-4M4 19h16" />
  ),

  upload: (
    <path d="M12 16V5m0 0 4 4m-4-4-4 4M4 19h16" />
  ),

  trash: (
    <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13M10 11v6M14 11v6" />
  ),

  search: (
    <>
      <circle
        cx="10.5"
        cy="10.5"
        r="6.5"
      />

      <path d="m20 20-4.7-4.7" />
    </>
  ),

  filter: (
    <path d="M3 5h18l-7 8v6l-4 2v-8z" />
  ),

  clock: (
    <>
      <circle
        cx="12"
        cy="12"
        r="8.5"
      />

      <path d="M12 7.5V12l3 2" />
    </>
  ),

  check: (
    <>
      <circle
        cx="12"
        cy="12"
        r="8.5"
      />

      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </>
  ),

  database: (
    <>
      <ellipse
        cx="12"
        cy="6"
        rx="7.5"
        ry="3"
      />

      <path d="M4.5 6v12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3V6" />

      <path d="M4.5 12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3" />
    </>
  ),

  file: (
    <path d="M6 3h7l5 5v13H6zM13 3v5h5" />
  ),

  refresh: (
    <path d="M20 12a8 8 0 1 1-2.3-5.6M20 4v5h-5" />
  ),

  close: (
    <path d="M6 6l12 12M18 6 6 18" />
  ),

  ruler: (
    <path d="M3.5 15.5 15.5 3.5l5 5-12 12zM7 12l2 2M10 9l2 2M13 6l2 2" />
  ),

  /*
   * Filled foot glyph used by the gait-analysis/event UI.
   */
  foot: (
    <g
      fill="currentColor"
      stroke="none"
    >
      <path d="M8.6 9.4C7.7 11.1 8 13.1 9.3 14.1c1.5 1.1 3.8 1 5.4.1 1.7-.9 2.6-2.2 2.2-3.9-.4-1.9-2-3.2-4.3-3.3-1.8-.1-3.4.6-4 2.4Z" />

      <ellipse
        cx="11.4"
        cy="18.4"
        rx="2.5"
        ry="3"
      />

      <circle
        cx="8.3"
        cy="6.6"
        r="1"
      />

      <circle
        cx="10.9"
        cy="5.3"
        r="1.1"
      />

      <circle
        cx="13.5"
        cy="5.2"
        r="1.05"
      />

      <circle
        cx="15.6"
        cy="6.1"
        r="0.9"
      />

      <circle
        cx="17.1"
        cy="7.6"
        r="0.72"
      />
    </g>
  ),

  tune: (
    <>
      <path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h11M19 18h1" />

      <circle
        cx="15"
        cy="6"
        r="2"
      />

      <circle
        cx="9"
        cy="12"
        r="2"
      />

      <circle
        cx="17"
        cy="18"
        r="2"
      />
    </>
  ),
};

export function Icon({
  name,
  className,
}: {
  name: IconName;
  className?: string;
}) {
  return (
    <svg
      className={cn('inline align-baseline', className)}
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[name]}
    </svg>
  );
}